<template>
    <PageSidebar :showError="isDirty && v$.$error">
        <template #sidebarTitle>
            <div class="space-x-3">
                <ButtonSecondaryIconSm @click="handleQuit">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span>
                    {{
                        mode === "create" ? $t("love.menu.add_review") : $t("love.menu.edit_review")
                    }}
                </span>
            </div>
        </template>
        <template #sidebarMain>
            <CardToggle :title="$t('shared.general')">
                <div class="space-y-5">
                    <FormAvatar
                        id="testimonialPhoto"
                        :title="$t('shared.avatar')"
                        :uploadLabel="$t('shared.upload')"
                        :deleteLabel="$t('shared.delete')"
                        :cropperLabels="{
                            crop: $t('shared.cropper.crop'),
                            discard: $t('shared.cropper.discard'),
                        }"
                        :file="testimonial.photo"
                        @loadFile="handleUploadAvatar"
                        :cropper-config="avatarCropperPreset"
                    />
                    <FormInput
                        id="testimonialName"
                        :title="$t('shared.first_last_name')"
                        v-model="testimonial.name"
                        @blur="v$.testimonial.name.$touch"
                        :error="v$.testimonial.name.$errors?.[0]?.$message"
                    />

                    <FormInputPreText
                        :title="$t('shared.website')"
                        v-model="testimonialWWW"
                        prefix="https://"
                    />
                    <FormInput
                        :title="$t('shared.position_company')"
                        v-model="testimonial.position"
                    />
                    <div id="testimonialRate" class="space-y-2">
                        <FormLabel>{{ $t("love.rating") }}</FormLabel>
                        <StarsPicker v-model="testimonial.rate" />
                    </div>
                    <div id="testimonialContent">
                        <FormRichText
                            ref="content"
                            :title="$t('love.review.review')"
                            buttonList="compact"
                            v-model="testimonial.content"
                            @blur="v$.testimonial.content.$touch"
                            :error="v$.testimonial.content.$errors?.[0]?.$message"
                        />
                    </div>
                    <div id="testimonialMedia" class="space-y-2">
                        <FormLabel>{{ $t("love.review.media") }}</FormLabel>
                        <FormSwitch v-model="uploadType" :options="uploadTypes" />

                        <FormFiles
                            v-if="uploadType === 'upload'"
                            v-model="imageFiles"
                            accept="image/*"
                            :labels="{
                                clickToUpload: $t('shared.uploader.click_to_upload'),
                                dragAndDrop: $t('shared.uploader.drag_and_drop'),
                                fileInfo: $t('shared.uploader.file_details'),
                            }"
                            :maxSize="10000"
                            :errorMessages="{
                                invalidSize: $t('shared.invalid.file_size_over'),
                                invalidType: $t('shared.invalid.file_type'),
                            }"
                            @fileSelected="handleUploadImage"
                            @fileRemoved="handleRemoveImage"
                        />
                        <div class="space-y-2" v-if="uploadType === 'gif'">
                            <FormInput
                                v-model="gifQuery"
                                @keyup.enter.native="handleGifSearch"
                                :placeholder="$t('love.review.search_gif_placeholder')"
                            />
                            <ButtonSecondaryLg class="w-full" @click="handleGifSearch">
                                {{ $t("love.review.search_gif") }}
                            </ButtonSecondaryLg>

                            <CardsGridGallery
                                v-if="gifCollection.length"
                                :images="gifCollection"
                                pickMode
                                @pick="handleGifPicked"
                            />
                        </div>
                    </div>

                    <CardsGridGallery
                        v-if="testimonial.gifs?.length"
                        :images="testimonial.gifs"
                        @remove="handleGifRemove"
                    />

                    <!-- <FormToggleSm :title="$t('love.review.use_generator')" v-model="useGenerator" /> -->

                    <!-- <FormSelect
                        v-if="useGenerator"
                        :label="$t('love.review.category')"
                        :title="$t('love.review.select_category')"
                        :options="categories"
                        v-model="generatorCategory"
                        :value="generatorCategory"
                    /> -->
                    <!-- <ButtonSecondarySm
                        v-if="generatorCategory"
                        @click="generateReview"
                        class="w-full"
                    >
                        {{ $t("love.review_generator.generate_another") }}
                    </ButtonSecondarySm> -->
                </div>
            </CardToggle>
            <CardToggle :title="$t('love.review.confirmation')" data-confirmation>
                <div class="space-y-5">
                    <FormToggleSm
                        :title="$t('love.review.send_request')"
                        v-model="sendConfirmationRequest"
                    />
                    <FormInput
                        v-if="sendConfirmationRequest"
                        ref="emailAddress"
                        :title="$t('love.review.email_address')"
                        :placeholder="$t('love.review.email_address_placeholder')"
                        v-model="testimonial.authorEmail"
                        @blur="v$.testimonial.authorEmail.$touch"
                        :error="v$.testimonial.authorEmail.$errors?.[0]?.$message"
                    />
                    <!-- <FormTextArea
                        v-if="sendConfirmationRequest"
                        ref="emailContent"
                        :title="$t('love.review.email_content')"
                        v-model="confirmationEmailContent"
                        @blur="v$.confirmationEmailContent.$touch"
                        :error="v$.confirmationEmailContent.$errors?.[0]?.$message"
                    /> -->
                </div>
            </CardToggle>
            <div class="lg:hidden p-5">
                <ButtonPrimaryMd
                    @click="handleFormSave()"
                    :disabled="formSaveBtnDisabled"
                    class="w-full"
                >
                    {{ saveButtonText }}
                </ButtonPrimaryMd>
            </div>
        </template>
        <template #sidebarFooter>
            <cErrorInfo v-if="isDirty && v$.$error" />
            <cSaveButton
                @save="handleFormSave"
                :disabled="formSaveBtnDisabled"
                :ctaEnabled="saveButtonText"
                :ctaDisabled="saveButtonText"
            />
        </template>
    </PageSidebar>
</template>

<script>
import { EventBus } from "@/shared/projects/event-bus";
import { PageSidebar } from "@easy/ui/page-components";
import {
    FormInput,
    FormInputPreText,
    FormLabel,
    FormSwitch,
    FormRichText,
    FormFiles,
    FormToggleSm,
    FormAvatar,
    filesModel,
} from "@easy/ui/forms";
import { CardToggle, CardsGridGallery } from "@easy/ui/cards";
import { ButtonPrimaryMd, ButtonSecondaryIconSm, ButtonSecondaryLg } from "@easy/ui/buttons";
import { ArrowLeftIcon } from "@easy/ui/icons";
import { StarsPicker } from "@easy/ui/stars";
import { minLength, required, email } from "@/shared/utils/forms/i18n-vuelidators";
import { mapActions, mapMutations } from "vuex";
import { makeLogger } from "@/shared/consola";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { useIsDirty } from "@/shared/utils/forms/useIsDirty";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import cErrorInfo from "@/shared/components/cErrorInfo.vue";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

const logger = makeLogger("ReviewDetails.vue");

export default {
    name: "ReviewDetails",
    components: {
        PageSidebar,
        FormInput,
        FormInputPreText,
        FormLabel,
        CardToggle,
        FormSwitch,
        FormRichText,
        FormFiles,
        FormToggleSm,
        StarsPicker,
        FormAvatar,
        ButtonSecondaryIconSm,
        ArrowLeftIcon,
        ButtonPrimaryMd,
        ButtonSecondaryLg,
        CardsGridGallery,
        cErrorInfo,
        cSaveButton,
    },
    props: {
        // v-model:testimonial
        testimonial: {
            type: Object,
            default: () => ({}),
        },
        testimonialBackup: {
            type: Object,
            default: () => ({}),
        },
        mode: {
            type: String,
            default: "create",
        },
    },
    emits: ["save", "cancel", "update:testimonial"],
    setup(props) {
        useFocusOnQueryField();
        const { isDirty } = useIsDirty(props.testimonialBackup, props.testimonial);
        const { askToConfirmIfNeeded } = useSaveOrDropModal();
        const { v$, validateForm } = useFormValidation();

        return {
            isDirty,
            askToConfirmIfNeeded,
            v$,
            validateForm,
        };
    },
    validations() {
        const rules = {
            testimonial: {
                name: { required, minLength: minLength(3) },
                content: { required, minLength: minLength(5) },
            },
        };
        if (this.sendConfirmationRequest) {
            // rules.confirmationEmailContent = { required };
            rules.testimonial.authorEmail = { required, email };
        }
        return rules;
    },
    data() {
        return {
            gifQuery: "",
            gifCollection: [],
            uploadType: "upload",
            uploadTypes: [
                {
                    label: this.$t("shared.upload"),
                    value: "upload",
                },
                {
                    label: this.$t("love.review.search_gif"),
                    value: "gif",
                },
            ],
            sendConfirmationRequest: false,
            confirmationEmailContent: "",
            // useGenerator: false,
            // generatorCategory: "",
            imageFiles: [],
        };
    },
    computed: {
        formSaveBtnDisabled() {
            //button cannot be disabled if user want to send Confirmation Request:
            if (this.sendConfirmationRequest) {
                return false;
            }
            // otherwise it depends on Dirty state of the form:
            return !this.isDirty;
        },
        testimonialWWW: {
            get() {
                return this.testimonial.www;
            },
            set(value) {
                const regex = /^(http|https):\/\/(.*)/gm;

                this.testimonial.www = value.replace(regex, "$2");
            },
        },
        categories() {
            return [
                {
                    value: "product",
                    label: this.$t("love.review_generator.product"),
                },
                { value: "e-book", label: this.$t("love.review_generator.ebook") },
                {
                    value: "training",
                    label: this.$t("love.review_generator.training"),
                },
                {
                    value: "consultations",
                    label: this.$t("love.review_generator.consultations"),
                },
                { value: "community", label: this.$t("love.review_generator.community") },
                { value: "service", label: this.$t("love.review_generator.service") },
                { value: "webinar", label: this.$t("love.review_generator.webinar") },
                { value: "event", label: this.$t("love.review_generator.event") },
            ];
        },
        saveButtonText() {
            const { sendConfirmationRequest, mode, $t } = this;
            return sendConfirmationRequest
                ? mode === "create"
                    ? $t("love.review.create_send_request")
                    : $t("love.review.save_send_request")
                : mode === "create"
                  ? $t("love.review.create_review")
                  : $t("love.review.save_review");
        },
        avatarCropperPreset() {
            return filesModel.CROPPER_PRESETS.CIRCLE(200, 1024);
        },
    },
    mounted() {
        EventBus.$on("quitReviewDetails", this.handleQuit);
    },
    beforeUnmount() {
        EventBus.$off("quitReviewDetails", this.handleQuit);
    },
    methods: {
        ...mapMutations(["showAlert"]),
        ...mapActions("love", [
            "searchGif",
            "uploadAvatar",
            "uploadAvatarNewTestimonial",
            "uploadImageTestimonial",
        ]),
        async handleUploadAvatar(file) {
            const { testimonial } = this;
            if (file === null) {
                return (testimonial.photo = "");
            }
            if (testimonial._created) {
                await this.uploadAvatarNewTestimonial({ testimonial, file });
            } else {
                await this.uploadAvatar({ testimonial, file });
            }
        },
        async handleUploadImage(file) {
            logger.trace("handleUploadImage", file);
            file.url = await this.uploadImageTestimonial({ testimonial: this.testimonial, file });
        },
        async handleRemoveImage(file) {
            const { images } = this.testimonial;
            const idx = images.indexOf(file.url || file.preview);
            if (idx !== -1) {
                images.splice(idx, 1);
            }
        },
        async handleFormSave() {
            const {
                sendConfirmationRequest,
                v$,
                validateForm,
                testimonial,
                confirmationEmailContent,
            } = this;
            const isFormCorrect = await validateForm();
            logger.trace("isFormCorrect", isFormCorrect, v$);
            if (isFormCorrect) {
                this.$emit("save", {
                    confirmation: {
                        send: sendConfirmationRequest,
                        email: testimonial.authorEmail,
                        content: confirmationEmailContent,
                    },
                    testimonial,
                });

                this.showAlert({
                    type: "success",
                    title:
                        this.mode === "create"
                            ? this.$t("love.reviews.testimonial_created")
                            : this.$t("love.reviews.testimonial_changed"),
                });
            }
        },
        // async generateReview() {
        //     if (!this.generatorCategory) {
        //         return;
        //     }
        //     // ATTENTION: this is crucial in order to make v-model work again!
        //     this.$refs.content.markPristine();
        //     this.testimonial.content = await generateTestimonialService.generate({
        //         category: this.generatorCategory,
        //         currentText: this.testimonial.content,
        //         lang: this.lang.value,
        //     });
        // },
        async handleGifSearch() {
            this.gifCollection = await this.searchGif(this.gifQuery);
        },
        handleGifPicked(gif) {
            this.testimonial.gifs.push(gif);
            this.gifQuery = "";
            this.gifCollection = [];
        },
        handleGifRemove(gif) {
            this.testimonial.gifs = this.testimonial.gifs.filter((url) => url !== gif);
        },
        async handleQuit() {
            const result = await this.askToConfirmIfNeeded({
                showModal: this.isDirty,
                onConfirmChanges: () => this.handleFormSave(),
            });
            if (!result) {
                this.$emit("cancel");
            }
        },
        // Action form the PARENT component!: (@see: SidebarReviewsList.confirmTestimonial)
        makeConfirmationRequest() {
            document
                .querySelector("[data-confirmation]")
                ?.scrollIntoView({ behavior: "smooth", block: "end" });
            this.sendConfirmationRequest = true;
            this.$nextTick(() => {
                // cannot focus inner textarea (@easy/ui problem)
                this.$refs.emailAddress.$el.querySelector("input")?.focus();
            });
        },
    },
    watch: {
        generatorCategory() {
            this.generateReview();
        },
        testimonial: {
            handler(newT, oldT) {
                if (oldT === undefined && Array.isArray(newT?.images)) {
                    this.imageFiles = [];
                    for (const url of newT.images) {
                        this.imageFiles.push(
                            filesModel.makeFile({
                                id: url,
                                name: "", //(url?.split(/[\\/]/).reverse() || [""])[0],
                                canRemove: true,
                                url,
                            })
                        );
                    }
                }
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>
